import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-63cfa979"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h1", null, " Your Applications "),
  /*#__PURE__*/_createElementVNode("h2", { class: "pb-5" }, " Keep track of your applications for birth records, benefits, and more. ")
], -1))
const _hoisted_2 = {
  key: 0,
  style: {"text-align":"center"}
}
const _hoisted_3 = {
  key: 1,
  class: "cards"
}

import { ref, computed, onMounted } from "vue";
import { CitizenApplicationsStatus } from './model';
import ApplicationStatusCard from './ApplicationStatusCard.vue';
import ApplicationCreationCard from "./ApplicationCreationCard.vue";
import KeyDocumentStatusCard from "./KeyDocumentStatusCard.vue";
import { DraftApplicationHeader } from '../applications/model';
import { isModuleEnabled, sessionState } from "@/core/session";
import { getApplicationStatus, getDraftApplications } from "./api";
import { Core } from "@minicity/core";

interface StartableApplication {
  name: string;
  description: string;
  applicationType: Core.EntityType | "identification";
  isDraft: boolean;
  isTabletOnly: boolean;
}

// Decide which new / draft application options to display

export default /*#__PURE__*/_defineComponent({
  __name: 'ApplicationStatusPanel',
  setup(__props) {

const activeApplications = ref<CitizenApplicationsStatus|null>(null);
const draftApplications = ref<DraftApplicationHeader[]|null>(null);

const refreshApplications = () => {
  activeApplications.value = null;
  draftApplications.value = null;
  getApplicationStatus(sessionState.session!.user.id).then((status) => activeApplications.value = status);
  getDraftApplications(sessionState.session!.user.id).then((drafts) => draftApplications.value = drafts);
};

onMounted(() => {
  refreshApplications();
});

const keyDocumentsToDisplay = computed(() => {
  const results = [];
  for (const keyDocumentType in sessionState.session?.user.keyDocuments) {
    if (["BIRTH_RECORD", "SOCIAL_SECURITY", "GEORGIA_DFCS_BENEFITS"].includes(keyDocumentType)) continue;

    const keyDocument = sessionState.session?.user.keyDocuments[keyDocumentType];
    if (!keyDocument) continue;

    if (!["REQUIRED","APPLIED","OBTAINED"].includes(keyDocument.status)) continue;
    
    results.push({keyDocumentType: keyDocumentType, keyDocument: keyDocument});
  }

  return results;
})

const applicationsAvailableToCreate = computed(() => {
  if (!draftApplications.value || !activeApplications.value) return [];

  const result:StartableApplication[] = [];

  const birthRecordsEnabled = isModuleEnabled('BIRTH_RECORDS');
  const socialSecurityEnabled = isModuleEnabled('SOCIAL_SECURITY');
  const benefitsEnabled = isModuleEnabled('GEORGIA_DFCS_BENEFITS');

  const draftIdApplication = draftApplications.value.find(draft => draft.applicationType=='identification');
  const draftBenefitsApplication = draftApplications.value.find(draft => draft.applicationType=='georgiaDfcsBenefits');

  const activeBirthRecordsApplication = activeApplications.value.applications.find(appl => appl.applicationType==Core.EntityType["Birth Record"]);
  const activeSocialSecurityApplication = activeApplications.value.applications.find(appl => [Core.EntityType["Social Security"], Core.EntityType["Online Social Security"]].includes(appl.applicationType));
  const activeBenefitsApplication = activeApplications.value.applications.find(appl => appl.applicationType==Core.EntityType["Georgia DFCS Benefits"]);
  
  const birthRecordsPending = birthRecordsEnabled && !activeBirthRecordsApplication;
  const socialSecurityPending = socialSecurityEnabled && !activeSocialSecurityApplication;
  const benefitsPending = benefitsEnabled && !activeBenefitsApplication;

  const legacySocialSecurityPending = socialSecurityPending && !isModuleEnabled('ONLINE_SOCIAL_SECURITY_APPLICATIONS');
  
  if (birthRecordsPending || legacySocialSecurityPending) {
    let idApplicationName;
    let idApplicationDescription;
    if (birthRecordsPending && legacySocialSecurityPending) {
      idApplicationName = "ID Application";
      idApplicationDescription = "Apply for Birth Records and/or a Social Security card";
    } else if (birthRecordsPending) {
      idApplicationName = "Birth Records Application";
      idApplicationDescription = "Apply for Birth Records";
    } else {
      idApplicationName = "Social Security Card Application";
      idApplicationDescription = "Apply for a Social Security card";
    }

    result.push({
      name: idApplicationName,
      description: idApplicationDescription,
      applicationType: "identification",
      isDraft: draftIdApplication != undefined,
      isTabletOnly: false
    });
  }

  if (socialSecurityPending && !legacySocialSecurityPending) {
    result.push({
      name: "Social Security Card Application",
      description: "Apply for a Social Security card",
      applicationType: Core.EntityType['Online Social Security'],
      isDraft: false,
      isTabletOnly: false
    });
  }

  if (benefitsPending) {
    result.push({
      name: "GA Benefits Application",
      description: "Apply for TANF / Food Stamps, Medicaid, and more",
      applicationType: Core.EntityType['Georgia DFCS Benefits'],
      isDraft: draftBenefitsApplication != undefined,
      isTabletOnly: false
    });
  }

  return result;
});

return (_ctx: any,_cache: any) => {
  const _component_LoadingSpinner = _resolveComponent("LoadingSpinner")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _hoisted_1,
    (!activeApplications.value || !draftApplications.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_LoadingSpinner)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeApplications.value.applications, (application, idx) => {
            return (_openBlock(), _createBlock(ApplicationStatusCard, {
              key: idx,
              application: application,
              onChanged: _cache[0] || (_cache[0] = ($event: any) => (refreshApplications())),
              class: "mb-4"
            }, null, 8, ["application"]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(keyDocumentsToDisplay), (keyDocEntry, idx) => {
            return (_openBlock(), _createBlock(KeyDocumentStatusCard, {
              key: idx,
              keyDocumentType: keyDocEntry.keyDocumentType,
              keyDocument: keyDocEntry.keyDocument,
              class: "mb-4"
            }, null, 8, ["keyDocumentType", "keyDocument"]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(applicationsAvailableToCreate), (application, idx) => {
            return (_openBlock(), _createBlock(ApplicationCreationCard, {
              key: idx,
              name: application.name,
              description: application.description,
              applicationType: application.applicationType,
              isDraft: application.isDraft,
              isTabletOnly: application.isTabletOnly,
              onChanged: _cache[1] || (_cache[1] = ($event: any) => (refreshApplications())),
              class: "mb-4"
            }, null, 8, ["name", "description", "applicationType", "isDraft", "isTabletOnly"]))
          }), 128))
        ]))
  ], 64))
}
}

})